/**
 * ActivityFeed (#1220) — one component behind two surfaces: the FA workspace
 * feed (`fa` set) and the cross-FA overview on /focus-areas (`fa` unset,
 * `showFa` to tag each row with its focus area).
 *
 * The backend serves facts (kind + demand + actor); the sentence lives here.
 * A null actor on application/invitation/comment rows is deliberate — outside
 * the viewer's FA the talent name is redacted (#1044) — and renders as
 * "Someone" / "New discussion".
 */

import { useState, type ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { makeStyles, tokens, Button, Spinner } from '@fluentui/react-components'
import {
  AddCircle16Regular,
  Comment16Regular,
  Flowchart16Regular,
  PeopleTeam16Regular,
} from '@fluentui/react-icons'
import { useActivity, type ActivityItem } from '../hooks/useActivity'
import { formatRelative } from '../lib/relativeTime'
import { activatable } from '../lib/activatable'
import { stageLabel } from '../lib/labels'
import { canonicalStage } from '../lib/stageHints'
import type { DemandStage } from '../types/demand'
import { bayerColors } from '../theme'

const PAGE = 25

const KIND_META: Record<ActivityItem['kind'], { icon: ReactElement; color: string }> = {
  stage: { icon: <Flowchart16Regular />, color: '#8b5cf6' },
  application: { icon: <PeopleTeam16Regular />, color: '#0891b2' },
  invitation: { icon: <PeopleTeam16Regular />, color: '#0891b2' },
  intake: { icon: <AddCircle16Regular />, color: '#059669' },
  comment: { icon: <Comment16Regular />, color: bayerColors.navyMid },
}

const useStyles = makeStyles({
  list: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  row: {
    display: 'grid',
    gridTemplateColumns: '24px 1fr',
    gap: tokens.spacingHorizontalS,
    alignItems: 'start',
    paddingBottom: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    ':last-child': { borderBottomStyle: 'none' },
  },
  iconBadge: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  line: { fontSize: '12.5px', color: tokens.colorNeutralForeground2, paddingTop: '3px' },
  title: {
    fontWeight: 600,
    color: bayerColors.navyMid,
    cursor: 'pointer',
    ':hover': { textDecoration: 'underline' },
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    marginTop: '1px',
  },
  faTag: {
    padding: '0 6px',
    borderRadius: '999px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground2,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
  },
  empty: { fontSize: '13px', color: tokens.colorNeutralForeground3, padding: '8px 0' },
  showMore: { alignSelf: 'flex-start' },
})

/**
 * History rows keep the retired vocabulary on purpose (CLAUDE.md); a move
 * reads as the live stage it aliases to — "Scoping → Resourcing", not
 * "Under Review → Open to Application".
 */
function liveStage(stage: string | null | undefined): DemandStage | null {
  return stage ? canonicalStage(stage as DemandStage) : null
}

/**
 * Rows that say nothing once aliased: a move that lands where it started
 * ("Resolved → Resolved"; "Open to Application → Staffed" is both Resourcing)
 * and the creation row ("— → New"), which the "New demand:" row already says.
 */
function isNoise(i: ActivityItem): boolean {
  return i.kind === 'stage' && (!i.from_stage || liveStage(i.from_stage) === liveStage(i.to_stage))
}

function parts(i: ActivityItem): { prefix: string; suffix?: string } {
  const actor = i.actor_name
  switch (i.kind) {
    case 'stage':
      return {
        prefix: `${actor ?? 'System'} moved`,
        suffix: `${stageLabel(liveStage(i.from_stage))} → ${stageLabel(liveStage(i.to_stage))}`,
      }
    case 'application':
      return { prefix: `${actor ?? 'Someone'} applied to` }
    case 'invitation':
      return { prefix: `${actor ?? 'Someone'} was invited to` }
    case 'intake':
      return { prefix: 'New demand:' }
    case 'comment':
      return { prefix: actor ? `${actor} commented on` : 'New discussion on' }
  }
}

export interface ActivityFeedProps {
  /** Scope to one focus area; omit for the cross-FA feed. */
  fa?: string
  /** Tag each row with its FA — for the cross-FA surface. */
  showFa?: boolean
}

export function ActivityFeed({ fa, showFa = false }: ActivityFeedProps) {
  const styles = useStyles()
  const navigate = useNavigate()
  const [limit, setLimit] = useState(PAGE)
  const { data: items, isLoading, isError, refetch } = useActivity(fa, limit)

  if (isLoading) return <Spinner size="tiny" label="Loading activity…" />
  // A failed request must not read as a quiet week. This feed is the whole of
  // /focus-areas for a leadership viewer, so "No activity yet" on a 500 makes
  // the entire system look empty on the first screen they see.
  if (isError) {
    return (
      <div className={styles.empty} role="alert" data-testid="activity-error">
        Couldn&apos;t load activity.
        <div style={{ marginTop: 8 }}>
          <Button size="small" appearance="secondary" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      </div>
    )
  }
  const rows = (items ?? []).filter((i) => !isNoise(i))
  // Empty means the server sent nothing. A page that was all noise keeps the
  // Show more below, so older activity stays reachable.
  if ((items ?? []).length === 0) {
    return <div className={styles.empty}>No activity yet.</div>
  }

  return (
    <div className={styles.list} data-testid="activity-feed">
      {rows.map((i) => {
        const meta = KIND_META[i.kind]
        const { prefix, suffix } = parts(i)
        // Comment rows land on the discussion itself; the rest on the demand.
        const target = i.kind === 'comment' ? `/demand/${i.demand_id}?tab=timeline` : `/demand/${i.demand_id}`
        return (
          <div key={`${i.kind}-${i.demand_id}-${i.ts}`} className={styles.row} data-testid={`activity-row-${i.kind}`}>
            <span
              className={styles.iconBadge}
              // A tint of the icon colour. color-mix, not a hex alpha suffix: some
              // colours are var(--…) since the Look & feel drawer (#1596), and
              // `var(…)1f` is not CSS — the badge lost its background.
              style={{ color: meta.color, background: `color-mix(in srgb, ${meta.color} 12%, transparent)` }}
              aria-hidden="true"
            >
              {meta.icon}
            </span>
            <div>
              <div className={styles.line}>
                {prefix}{' '}
                <span className={styles.title} role="link" {...activatable(() => navigate(target))}>
                  {i.demand_title}
                </span>
                {suffix ? ` · ${suffix}` : ''}
              </div>
              <div className={styles.meta}>
                {showFa && i.fa_code && <span className={styles.faTag}>{i.fa_code}</span>}
                <span title={new Date(i.ts).toLocaleString()}>{formatRelative(i.ts)}</span>
              </div>
            </div>
          </div>
        )
      })}
      {/* Keyed on what the server sent, not on what survived the filter. */}
      {(items?.length ?? 0) >= limit && limit < 100 && (
        <Button
          appearance="subtle"
          size="small"
          className={styles.showMore}
          // The endpoint caps limit at 100 — growing past it would 422.
          onClick={() => setLimit((l) => Math.min(l + PAGE, 100))}
          data-testid="activity-show-more"
        >
          Show more
        </Button>
      )}
    </div>
  )
}
